import type { ReaderBook } from '@/services/booksApi';
import { analytics } from '@/services/analytics';
import { pinBook, unpinBook } from '@/services/offlineSettings';
import { listStoredAssets, purgeBook } from './assetStore';
import { bufferScheduler } from './bufferScheduler';
import { preflightDownload, type DownloadAssessment } from './downloadPreflight';

/**
 * The two things a "Download this book" control does, behind one call each,
 * so the control itself stays presentational: ask for a book, which runs
 * the size preflight and pins the book only if it fits; and remove one,
 * which unpins it and empties its files.
 *
 * Neither waits for a download. Pinning wakes the scheduler, which fetches
 * the book behind the open book's window and reports progress in its
 * status; the control subscribes to that.
 */

export type DownloadRequest =
  | { pinned: true; assessment: DownloadAssessment }
  | { pinned: false; assessment: DownloadAssessment };

export interface BookDownloadDeps {
  preflight: typeof preflightDownload;
  pinBook: (bookId: string, voiceKey: string) => void;
  unpinBook: (bookId: string) => void;
  /** Empty the book, leaving the named assets alone. */
  purgeBook: (bookId: string, keepUrls: string[]) => Promise<void>;
  /** What the player and the reader are using right now. */
  inUseAssetUrls: () => string[];
  /** Re-plan the buffer, so the open book refills after a removal. */
  poke: () => void;
  heldBytes: (bookId: string) => number;
  track: (event: string, properties: Record<string, unknown>) => void;
}

/**
 * Pin the book in this voice if it fits; otherwise refuse, with the
 * assessment so the control can show `DOWNLOAD_REFUSED_MESSAGE`. The
 * open book is named because its space is never reclaimable.
 */
export const requestBookDownload = (
  book: ReaderBook,
  voiceKey: string,
  openBookId: string | null,
  deps: BookDownloadDeps = defaultBookDownloadDeps,
): DownloadRequest => {
  const assessment = deps.preflight(book, voiceKey, openBookId);
  const properties = {
    book_id: book.id,
    voice_key: voiceKey,
    bytes: assessment.bytes,
    estimated: assessment.estimated,
    needed_bytes: assessment.neededBytes,
    available_bytes: assessment.availableBytes,
  };
  if (!assessment.fits) {
    deps.track('Book Download Refused', properties);
    return { pinned: false, assessment };
  }
  deps.pinBook(book.id, voiceKey);
  deps.track('Book Pinned', properties);
  return { pinned: true, assessment };
};

/**
 * Unpin and empty the book. If it is the one being listened to, the buffer
 * refills its window straight away; that is the feature, and the control's
 * confirm copy says so.
 *
 * What is in use stays: the chapter that is playing is being read from its
 * file, and deleting that stops playback a few seconds later, when the
 * audio element asks for its next range. Everything else of the book goes.
 */
export const removeBookDownload = async (bookId: string, deps: BookDownloadDeps = defaultBookDownloadDeps): Promise<void> => {
  const before = deps.heldBytes(bookId);
  // Unpin first, so the scheduler cannot refill the book between the purge
  // and the re-plan.
  deps.unpinBook(bookId);
  await deps.purgeBook(bookId, deps.inUseAssetUrls());
  deps.poke();
  deps.track('Book Unpinned', { book_id: bookId, bytes: Math.max(0, before - deps.heldBytes(bookId)) });
};

export const defaultBookDownloadDeps: BookDownloadDeps = {
  preflight: preflightDownload,
  pinBook,
  unpinBook,
  purgeBook: (bookId, keepUrls) => purgeBook(bookId, { keepUrls }),
  inUseAssetUrls: () => bufferScheduler.inUseAssetUrls(),
  poke: () => bufferScheduler.poke(),
  heldBytes: (bookId) => listStoredAssets(bookId).reduce((sum, asset) => sum + asset.bytes, 0),
  track: (event, properties) => analytics.track(event, properties),
};
